import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown, Copy, Check } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/client/components/ui/tooltip';
import type { ClientUserResponse } from '@shared/types';

const USER_ROLES: Record<number, string> = {
  1: 'Admin',
  2: 'Manager',
  3: 'User',
};

const AUTH_TYPES: Record<number, string> = {
  1: 'Standard',
  2: 'SSO',
  3: 'LDAP',
};

/** Truncated cell with tooltip on hover */
function TruncatedCell({ value, maxWidth = 200 }: { value: string; maxWidth?: number }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="block truncate"
          style={{ maxWidth: `${maxWidth}px` }}
        >
          {value}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{value}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/** Clickable email cell that copies on click */
function CopyableEmailCell({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="group flex items-center gap-1 max-w-[200px] hover:text-primary transition-colors cursor-pointer text-left"
      title={`Click to copy: ${email}`}
    >
      <span className="truncate">{email}</span>
      {copied ? (
        <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );
}

export const columns: ColumnDef<ClientUserResponse>[] = [
  {
    accessorKey: 'userId',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          ID
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => <span className="font-mono">{row.getValue('userId')}</span>,
  },
  {
    id: 'name',
    accessorFn: (row) => {
      const first = row.firstName || '';
      const last = row.lastName || '';
      return `${first} ${last}`.trim() || '-';
    },
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const name = row.getValue('name') as string;
      return <TruncatedCell value={name} maxWidth={150} />;
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Email
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const email = row.getValue('email') as string;
      return <CopyableEmailCell email={email} />;
    },
  },
  {
    accessorKey: 'userRole',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Role
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const role = row.getValue('userRole') as number | null;
      return role ? USER_ROLES[role] || `Role ${role}` : '-';
    },
  },
  {
    accessorKey: 'authenticationType',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="hidden lg:flex"
        >
          Auth Type
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const authType = row.getValue('authenticationType') as number;
      return (
        <span className="hidden lg:inline">
          {AUTH_TYPES[authType] || `Type ${authType}`}
        </span>
      );
    },
    meta: {
      className: 'hidden lg:table-cell',
    },
  },
  {
    accessorKey: 'recieveEmails',
    header: () => <span className="hidden xl:inline">Emails</span>,
    cell: ({ row }) => {
      const value = row.getValue('recieveEmails') as number | null;
      return <span className="hidden xl:inline">{value ? 'Yes' : 'No'}</span>;
    },
    meta: {
      className: 'hidden xl:table-cell',
    },
  },
  {
    accessorKey: 'locked',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const locked = row.getValue('locked') as number | null;
      return locked ? (
        <span className="text-destructive">Locked</span>
      ) : (
        <span className="text-green-600">Active</span>
      );
    },
  },
];
